import React, { useMemo, useState } from 'react'
import { IonItem, IonGrid, IonRow, IonCol, IonLabel, IonRippleEffect, IonLoading } from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import LabelComponent from '../../../components/label/Label.component'
import { getPatientSearchState } from '../selectors'
import { IPatientSearchResult, IPatientSearchState } from '../store/patientSearch.types'
import {
  setHasPatient,
  setNewPatient,
  setSearchImport,
  setSelectedOption,
} from '../../patientInformation/store/patientInformation.actions'
import { PatientInformationConstants } from '../../patientInformation/store/patientInformation.constants'
import { getPatientRecord, setIsPatientDataLoading } from '../store/patientSearch.actions'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { useOfflineStatus } from '../../../utilities/isOnline.util'
import { getServiceIdSelector } from '../../user/selectors'
import './PatientSearchResults.content.css'
import NewPatientButton from '../../../components/patientSearch/NewPatientButton.component'
import { clearPatientSearchErrors } from '../../../utilities/runRulesValidation.util'
import { getRulesSelector } from '../../rules/selectors'
import { IRulesState } from '../../rules/store/rules.types'
import { showLoader } from '../../../components/loader/store/loader.actions'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'

const PatientSearchResultsContent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { isTablet } = useDeviceMetrics()
  const serviceId = useSelector(getServiceIdSelector)
  const rules: IRulesState = useSelector(getRulesSelector)
  const patientSearchState: IPatientSearchState = useSelector(getPatientSearchState)
  const {
    searchResults,
    areResultsLoading,
    showImportDataConfirmationAlert,
    totalAvailableResults,
    totalSearchResults,
    showSharedServiceSearchResultsMessage,
  } = patientSearchState
  const isOfflineStatus = useOfflineStatus()
  const [showMaxPatientSearchResultsExceedMsg, setShowMaxPatientSearchResultsExceedMsg] = useState(false)
  const isFromPulsara = history.location.pathname.includes('/pulsara')

  useMemo(() => {
    if (totalAvailableResults > totalSearchResults) {
      setShowMaxPatientSearchResultsExceedMsg(true)
    } else {
      setShowMaxPatientSearchResultsExceedMsg(false)
    }
  }, [totalAvailableResults, totalSearchResults])

  const onRowClick = (data: IPatientSearchResult) => {
    if (showImportDataConfirmationAlert) {
      const alert: IAlertState = {
        header: '',
        message: t('patientSearch.importPatientDataOverWriteAlertMessage'),
        buttons: [
          {
            text: t('common.buttons.No'),
          },
          {
            text: t('common.buttons.Yes'),
            role: 'destructive',
            handler: () => getPatientData(data.ptid),
          },
        ],
      }
      dispatch(showAlert(alert))
    } else {
      getPatientData(data.ptid)
    }
  }

  const getPatientData = (ptid: number) => {
    rules.errors = clearPatientSearchErrors(rules.errors)
    if (!isFromPulsara) {
      dispatch(setIsPatientDataLoading(true))
      dispatch(setNewPatient(false))
      dispatch(setSearchImport(true))
    } else {
      dispatch(showLoader({ isOpen: true, message: t('pulsara.loading.patientData') }))
    }
    dispatch(getPatientRecord(ptid))
    if (!isFromPulsara) {
      dispatch(setHasPatient(true))
      if (isTablet) dispatch(setSelectedOption(PatientInformationConstants.PATIENT_DEMOGRAPHICS_OPTION))
      else history.push(TabConstants.PATIENT_DEMOGRAPHICS)
    }
  }

  return (
    <>
      {isTablet && !isFromPulsara && (
        <IonRow>
          <IonCol>
            <IonItem color='light' lines='none'>
              <IonLabel>{t('patientSearch.searchResults')}</IonLabel>
            </IonItem>
          </IonCol>
          <IonCol>
            <NewPatientButton />
          </IonCol>
        </IonRow>
      )}
      {!isOfflineStatus && showMaxPatientSearchResultsExceedMsg && !areResultsLoading && (
        <IonItem color='light' lines='none'>
          <IonLabel className='ion-text-wrap'>
            {t('patientSearch.totalSearchResultsExceedMessage1')} {totalAvailableResults}
            {t('patientSearch.totalSearchResultsExceedMessage2')} {totalSearchResults}{' '}
            {t('patientSearch.totalSearchResultsExceedMessage3')}
          </IonLabel>
        </IonItem>
      )}
      {!isOfflineStatus && showSharedServiceSearchResultsMessage && !areResultsLoading && (
        <IonItem color='light' lines='none'>
          <IonLabel className='ion-text-wrap'>
            <span style={{ color: 'var(--color-danger-text)', display: 'inline' }}> * </span>{' '}
            {t('patientSearch.sharedServiceSearchResultsMessage')}
          </IonLabel>
        </IonItem>
      )}
      {searchResults &&
        searchResults.length !== 0 &&
        !isOfflineStatus &&
        searchResults.map((resultData: IPatientSearchResult) => (
          <IonItem
            button
            detail={false}
            data-testid={resultData.ptid}
            key={resultData.ptid}
            onClick={() => onRowClick(resultData)}
            className='ion-no-padding ion-activatable'>
            <IonGrid>
              <IonRow className='ion-align-items-start'>
                <IonCol>
                  <IonLabel color='medium' className='patient-name-label'>
                    {+serviceId !== +resultData.serviceId && (
                      <span style={{ color: 'var(--color-danger-text)', display: 'inline' }}> * </span>
                    )}
                    {t('patientSearch.patientName')}
                  </IonLabel>
                  <LabelComponent isPHI className='text-wrap' label={resultData.name || t('common.NotAvailable')} />
                </IonCol>
                <IonCol offset='2'>
                  <IonLabel color='medium'>
                    <h3>{t('patientSearch.dOB')}</h3>
                  </IonLabel>
                  <LabelComponent isPHI label={resultData.dob || t('common.NotAvailable')} />
                </IonCol>
              </IonRow>
              <IonRow className='ion-align-items-start'>
                <IonCol>
                  <IonLabel color='medium'>
                    <h3>{t('patientSearch.sSN')}</h3>
                  </IonLabel>
                  <LabelComponent isPHI label={resultData.last4Ssn || t('common.NotAvailable')} />
                </IonCol>
                <IonCol offset='2'>
                  <IonLabel color='medium'>
                    <h3>{t('patientSearch.lastRecordSystem')}</h3>
                  </IonLabel>
                  <LabelComponent isPHI label={resultData.lastRecordSystem || t('common.NotAvailable')} />
                </IonCol>
              </IonRow>
              {resultData.lastRecordService && resultData.lastRecordService !== resultData.lastRecordSystem && (
                <IonRow className='ion-align-items-start'>
                  <IonCol>
                    <IonLabel color='medium'>
                      <h3>{t('patientSearch.lastRecordService')}</h3>
                    </IonLabel>
                    <LabelComponent isPHI label={resultData.lastRecordService || t('common.NotAvailable')} />
                  </IonCol>
                </IonRow>
              )}
            </IonGrid>
            <IonRippleEffect />
          </IonItem>
        ))}
      {(!searchResults || searchResults.length === 0) && !areResultsLoading && !isOfflineStatus && (
        <IonItem data-testid='NoOptions'>
          <IonLabel>{t('common.NoRecordsFound')}</IonLabel>
        </IonItem>
      )}
      {isOfflineStatus && (
        <IonItem color='light' lines='none'>
          <IonLabel className='ion-text-wrap'>{t('patientSearch.offlinePatientSearchUnavailableMessage')}</IonLabel>
        </IonItem>
      )}
      {areResultsLoading && <IonLoading data-testid='Loader' isOpen animated showBackdrop spinner={'crescent'} />}
    </>
  )
}

export default PatientSearchResultsContent
